/**
 * Cogo Action -> Gemini FunctionDeclaration converter
 *
 * Maps exposed Cogo ActionFlow action names to Gemini-compatible
 * function declarations so the LLM can call them via function-calling.
 */

import type { GeminiFunctionDeclaration } from './gemini-client'

export const ACTION_SCHEMAS: Record<string, GeminiFunctionDeclaration> = {
  restApi: {
    name: 'restApi',
    description: 'Make an HTTP request to a REST API endpoint',
    parameters: {
      type: 'object',
      properties: {
        url: { type: 'string', description: 'The URL to fetch' },
        method: { type: 'string', description: 'HTTP method (GET, POST, PUT, DELETE)' },
        headers: { type: 'object', description: 'Request headers' },
        body: { type: 'object', description: 'Request body (for POST/PUT)' },
        resultTarget: { type: 'string', description: 'State path to store result' },
      },
      required: ['url'],
    },
  },
  setState: {
    name: 'setState',
    description: 'Set a value in the application state',
    parameters: {
      type: 'object',
      properties: {
        target: { type: 'string', description: 'State path (e.g. ui.title)' },
        value: { description: 'Value to set (any type)' },
        operation: {
          type: 'string',
          description: 'Operation: set, append, upsert, removeWhere, updateWhere',
        },
      },
      required: ['target', 'value'],
    },
  },
  supabaseQuery: {
    name: 'supabaseQuery',
    description: 'Query a Supabase database table',
    parameters: {
      type: 'object',
      properties: {
        table: { type: 'string', description: 'Table name' },
        select: { type: 'string', description: 'Columns to select' },
        filter: { type: 'object', description: 'Filter conditions' },
        resultTarget: { type: 'string', description: 'State path to store result' },
      },
      required: ['table'],
    },
  },
  supabaseRpc: {
    name: 'supabaseRpc',
    description: 'Call a Supabase RPC function',
    parameters: {
      type: 'object',
      properties: {
        function: { type: 'string', description: 'RPC function name' },
        params: { type: 'object', description: 'Function parameters' },
        resultTarget: { type: 'string', description: 'State path to store result' },
      },
      required: ['function'],
    },
  },
  graphqlAction: {
    name: 'graphqlAction',
    description: 'Execute a GraphQL query or mutation',
    parameters: {
      type: 'object',
      properties: {
        endpoint: { type: 'string', description: 'GraphQL endpoint URL' },
        query: { type: 'string', description: 'GraphQL query string' },
        variables: { type: 'object', description: 'Query variables' },
        resultTarget: { type: 'string', description: 'State path to store result' },
      },
      required: ['endpoint', 'query'],
    },
  },
  navigate: {
    name: 'navigate',
    description: 'Navigate to a page within the app',
    parameters: {
      type: 'object',
      properties: {
        page: { type: 'string', description: 'Target page ID or route' },
        params: { type: 'object', description: 'Route parameters' },
      },
      required: ['page'],
    },
  },
  showToast: {
    name: 'showToast',
    description: 'Show a toast notification to the user',
    parameters: {
      type: 'object',
      properties: {
        message: { type: 'string', description: 'Toast message' },
        type: { type: 'string', description: 'Toast type: success, error, info, warning' },
      },
      required: ['message'],
    },
  },
  log: {
    name: 'log',
    description: 'Log a message for debugging',
    parameters: {
      type: 'object',
      properties: {
        message: { type: 'string', description: 'Message to log' },
      },
      required: ['message'],
    },
  },
}

/**
 * Build Gemini FunctionDeclaration array from a list of exposed action names.
 *
 * Unknown action names are silently filtered out, so callers can safely
 * pass user-provided lists without pre-validation.
 */
export function buildFunctionDeclarations(
  exposedActions: string[],
): GeminiFunctionDeclaration[] {
  return exposedActions
    .filter((name) => ACTION_SCHEMAS[name] != null)
    .map((name) => ACTION_SCHEMAS[name])
}
